"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { getReadableImageUrl } from "@/lib/directus";

interface DirectusInterfaceData {
  logo?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  button?: {
    href: string;
    label: string;
  };
  button2?: {
    href: string;
    label: string;
  };
  phone?: {
    title: string;
  };
  icon_link?: {
    url: string;
    title: string;
    icon: {
      id: string;
      filename_download: string;
    };
  };
  menu?: {
    menu_items: {
      menu_items_id: {
        label: string;
        href: string;
        submenu?: {
          label: string;
          href: string;
          submenu?: {
            label: string;
            href: string;
          }[];
        }[];
      };
    }[];
  };
  small_menu?: {
    menu_items: {
      menu_items_id: {
        label: string;
        href: string;
        submenu?: {
          label: string;
          href: string;
        }[];
      };
    }[];
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  error?: boolean;
  fallback?: boolean;
  message?: string;
  timestamp?: number;
}

export const fields = [
  "logo.alt",
  "logo.image_file.image.id",
  "logo.image_file.image.filename_download",

  "button.href",
  "button.label",

  "button2.href",
  "button2.label",

  "phone.title",

  "icon_link.url",
  "icon_link.title",
  "icon_link.icon.id",
  "icon_link.icon.filename_download",

  "menu.menu_items.menu_items_id.label",
  "menu.menu_items.menu_items_id.href",
  "menu.menu_items.menu_items_id.submenu.label",
  "menu.menu_items.menu_items_id.submenu.href",
  "menu.menu_items.menu_items_id.submenu.submenu.label",
  "menu.menu_items.menu_items_id.submenu.submenu.href",

  "small_menu.menu_items.menu_items_id.label",
  "small_menu.menu_items.menu_items_id.href",
  "small_menu.menu_items.menu_items_id.submenu.label",
  "small_menu.menu_items.menu_items_id.submenu.href",

  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
] as const;

interface HeaderHamburgerNavProps {
  data?: DirectusInterfaceData;
}

export default function HeaderHamburgerNav({ data }: HeaderHamburgerNavProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);
  const [openThirdLevel, setOpenThirdLevel] = useState<string[]>([]);

  useEffect(() => {
    if (data?.menu?.menu_items) {
      const thirdLevelIds: string[] = [];

      data.menu.menu_items.forEach((menuItem, menuIndex) => {
        if (menuItem.menu_items_id.submenu) {
          menuItem.menu_items_id.submenu.forEach((subItem, subIndex) => {
            if (subItem.submenu && subItem.submenu.length > 0) {
              thirdLevelIds.push(`third-${menuIndex}-${subIndex}`);
            }
          });
        }
      });

      setOpenThirdLevel(thirdLevelIds);
    }
  }, [data]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    document.body.style.overflow = !isOpen ? "hidden" : "unset";
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    document.body.style.overflow = "unset";
  };

  const toggleSubmenu = (id: string) => {
    setOpenSubmenus((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleThirdLevel = (id: string) => {
    setOpenThirdLevel((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const isSubmenuOpen = (id: string) => openSubmenus.includes(id);
  const isThirdLevelOpen = (id: string) => openThirdLevel.includes(id);

  const arrowClass =
    "inline-block w-2 h-2 ml-2 border-r-2 border-b-2 border-primary transform transition-transform duration-200";

  const SubMenuItem = ({
    title,
    href,
    id,
    children,
    isMainItem = false,
  }: {
    title: string;
    href: string;
    id: string;
    children: React.ReactNode;
    isMainItem?: boolean;
  }) => (
    <div>
      <div
        className="flex items-center justify-between cursor-pointer md:cursor-default"
        onClick={() => toggleSubmenu(id)}
      >
        {isMainItem ? (
          href === "/" ? (
            <Link
              href="/"
              onClick={handleLinkClick}
              className="text-primary text-lg font-semibold font-greycliff uppercase"
            >
              {title}
            </Link>
          ) : href === "#" ? (
            <span className="text-primary text-lg font-semibold font-greycliff uppercase">
              {title}
            </span>
          ) : (
            <Link
              href={href || "#"}
              onClick={handleLinkClick}
              className="text-primary text-lg font-semibold font-greycliff uppercase"
            >
              {title}
            </Link>
          )
        ) : (
          <div className="text-primary text-base font-semibold font-greycliff uppercase">
            {href === "/" ? (
              <Link href="/" onClick={handleLinkClick}>
                {title}
              </Link>
            ) : href === "#" ? (
              <span>{title}</span>
            ) : (
              <Link href={href || "#"} onClick={handleLinkClick}>
                {title}
              </Link>
            )}
          </div>
        )}
        <span
          className={`md:hidden ${arrowClass} ${
            isSubmenuOpen(id) ? "rotate-[225deg]" : "rotate-45"
          }`}
        />
      </div>
      <div
        className={`mt-2 md:block ${isSubmenuOpen(id) ? "block" : "hidden"}`}
      >
        {children}
      </div>
    </div>
  );

  const ThirdLevelItem = ({
    title,
    href,
    id,
    children,
  }: {
    title: string;
    href: string;
    id: string;
    children: React.ReactNode;
  }) => (
    <div>
      <div
        className="flex items-center justify-between cursor-pointer md:cursor-default"
        onClick={() => toggleThirdLevel(id)}
      >
        <div className="text-primary text-base font-bold font-greycliff leading-relaxed">
          {href === "/" ? (
            <Link href="/" onClick={handleLinkClick}>
              {title}
            </Link>
          ) : href === "#" ? (
            <span>{title}</span>
          ) : (
            <Link href={href || "#"} onClick={handleLinkClick}>
              {title}
            </Link>
          )}
        </div>
        <span
          className={`md:hidden ${arrowClass} ${
            isThirdLevelOpen(id) ? "rotate-[225deg]" : "rotate-45"
          }`}
        />
      </div>
      <div
        className={`mt-2 md:block ${isThirdLevelOpen(id) ? "block" : "hidden"}`}
      >
        {children}
      </div>
    </div>
  );

  return (
    <nav className="w-full bg-white sticky top-0 z-[9999999]">
      <div className="container mx-auto px-6 2xl:px-16 py-3 lg:py-9 flex justify-between items-center">
        <div className="relative z-[9999]">
          <a href="/" onClick={handleLinkClick}>
            {data?.logo?.image_file?.image?.id &&
              data?.logo?.image_file?.image?.filename_download && (
                <Image
                  src={getReadableImageUrl(
                    data.logo.image_file.image.id,
                    data.logo.image_file.image.filename_download
                  )}
                  alt={data.logo.alt}
                  width={232}
                  height={57}
                  quality={90}
                  className="w-[11rem] lg:w-[14.5rem] h-auto"
                />
              )}
          </a>
        </div>
        <div className="flex items-center gap-3 lg:gap-6">
          <div className="flex items-center gap-6">
            {data?.button && (
              <div className="bg-primary rounded-tr-[40px] rounded-br-[40px] h-[41px] px-8 hidden lg:flex items-center">
                {data.button.href === "#" ? (
                  <span className="text-white text-xl font-normal font-greycliff leading-7 tracking-tight [text-wrap:nowrap]">
                    {data.button.label}
                  </span>
                ) : (
                  <Link
                    href={data.button.href || "#"}
                    className="text-white text-xl font-normal font-greycliff leading-7 tracking-tight [text-wrap:nowrap]"
                    onClick={handleLinkClick}
                  >
                    {data.button.label}
                  </Link>
                )}
              </div>
            )}
            <div className="flex items-center gap-5">
              {data?.phone && (
                <Link
                  href={`tel:${data.phone.title || ""}`}
                  className="text-primary text-xl font-medium font-greycliff leading-7 tracking-tight p-4 lg:p-0 flex items-center"
                  onClick={handleLinkClick}
                >
                  <span className="lg:inline hidden">{data.phone.title}</span>
                  <span className="lg:hidden inline-block w-5 h-5">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      className="w-full h-full fill-primary"
                    >
                      <path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z" />
                    </svg>
                  </span>
                </Link>
              )}
              <div className="opacity-90 hidden lg:flex items-center gap-3.5">
                {data?.icon_link &&
                  (data.icon_link.url === "#" ? (
                    <span>
                      <Image
                        src={getReadableImageUrl(
                          data.icon_link.icon.id,
                          data.icon_link.icon.filename_download
                        )}
                        alt={data.icon_link.title}
                        width={18}
                        height={19}
                        quality={90}
                      />
                    </span>
                  ) : (
                    <Link
                      href={data.icon_link.url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleLinkClick}
                    >
                      <Image
                        src={getReadableImageUrl(
                          data.icon_link.icon.id,
                          data.icon_link.icon.filename_download
                        )}
                        alt={data.icon_link.title}
                        width={18}
                        height={19}
                        quality={90}
                      />
                    </Link>
                  ))}
              </div>
            </div>
          </div>
          <button
            onClick={toggleMenu}
            className="w-14 h-8 flex items-center justify-center relative group"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            <span className="flex flex-col justify-center items-center w-8 h-8">
              <span
                className={`
                  block h-0.5 w-8 bg-primary rounded-sm transition-all duration-300 ease-out
                  ${isOpen ? "rotate-45" : "-translate-y-1"}
                `}
              />
              <span
                className={`
                  block h-0.5 w-8 bg-primary rounded-sm transition-all duration-300 ease-out
                  ${isOpen ? "opacity-0" : "opacity-100"}
                `}
              />
              <span
                className={`
                  block h-0.5 w-8 bg-primary rounded-sm transition-all duration-300 ease-out
                  ${
                    isOpen
                      ? "-rotate-45 -translate-y-[0.23rem]"
                      : "translate-y-1"
                  }
                `}
              />
            </span>
          </button>
        </div>
      </div>
      <div
        className={`fixed inset-0 bg-white transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-[150vw]"
        } z-50 mt-16 lg:mt-32`}
      >
        <div className="bg-light h-full overflow-y-auto">
          <div className="w-full lg:h-full flex flex-col lg:flex-row pb-8 lg:pb-0">
            <div className="w-full lg:w-[80%] p-4 md:p-6">
              <ul className="flex flex-col md:flex-row flex-wrap">
                {data?.menu?.menu_items.map((menuItem, index) => {
                  const item = menuItem.menu_items_id;
                  const hasSubmenu = item.submenu && item.submenu.length > 0;

                  return (
                    <li
                      key={index}
                      className="w-full lg:w-1/3 xl:w-[16.6%] xl:px-4 mb-10"
                    >
                      {hasSubmenu ? (
                        <SubMenuItem
                          title={item.label}
                          href={item.href}
                          id={`main-${index}`}
                          isMainItem
                        >
                          <ul className="space-y-3">
                            {item.submenu?.map((subItem, subIndex) => {
                              const hasThirdLevel =
                                subItem.submenu && subItem.submenu.length > 0;

                              return (
                                <li
                                  key={`sub-${index}-${subIndex}`}
                                  className="my-3"
                                >
                                  {hasThirdLevel ? (
                                    <ThirdLevelItem
                                      title={subItem.label}
                                      href={subItem.href}
                                      id={`third-${index}-${subIndex}`}
                                    >
                                      <ul className="ml-5 space-y-2">
                                        {subItem.submenu?.map(
                                          (thirdItem, thirdIndex) => (
                                            <li
                                              key={`third-${index}-${subIndex}-${thirdIndex}`}
                                              className="my-3"
                                            >
                                              {thirdItem.href === "/" ? (
                                                <Link
                                                  href="/"
                                                  className="text-primary text-sm font-light font-greycliff hover:font-medium transition-all group flex items-center gap-2 leading-relaxed"
                                                  onClick={handleLinkClick}
                                                >
                                                  {thirdItem.label}
                                                  <Image
                                                    src="/images/nav-dot.svg"
                                                    alt="nav dot"
                                                    width={4}
                                                    height={4}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                  />
                                                </Link>
                                              ) : thirdItem.href === "#" ? (
                                                <span className="text-primary text-sm font-light font-greycliff hover:font-medium transition-all group flex items-center gap-2 leading-relaxed">
                                                  {thirdItem.label}
                                                  <Image
                                                    src="/images/nav-dot.svg"
                                                    alt="nav dot"
                                                    width={4}
                                                    height={4}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                  />
                                                </span>
                                              ) : (
                                                <Link
                                                  href={thirdItem.href || "#"}
                                                  className="text-primary text-sm font-light font-greycliff hover:font-medium transition-all group flex items-center gap-2 leading-relaxed"
                                                  onClick={handleLinkClick}
                                                >
                                                  {thirdItem.label}
                                                  <Image
                                                    src="/images/nav-dot.svg"
                                                    alt="nav dot"
                                                    width={4}
                                                    height={4}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                  />
                                                </Link>
                                              )}
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    </ThirdLevelItem>
                                  ) : subItem.href === "/" ? (
                                    <Link
                                      href="/"
                                      className="text-primary text-base font-bold font-greycliff hover:font-bold transition-all group flex items-center gap-2 leading-relaxed"
                                      onClick={handleLinkClick}
                                    >
                                      {subItem.label}
                                      <Image
                                        src="/images/nav-dot.svg"
                                        alt="nav dot"
                                        width={4}
                                        height={4}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                      />
                                    </Link>
                                  ) : subItem.href === "#" ? (
                                    <span className="text-primary text-base font-bold font-greycliff hover:font-bold transition-all group flex items-center gap-2 leading-relaxed">
                                      {subItem.label}
                                      <Image
                                        src="/images/nav-dot.svg"
                                        alt="nav dot"
                                        width={4}
                                        height={4}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                      />
                                    </span>
                                  ) : (
                                    <Link
                                      href={subItem.href || "#"}
                                      className="text-primary text-base font-bold font-greycliff hover:font-bold transition-all group flex items-center gap-2 leading-relaxed"
                                      onClick={handleLinkClick}
                                    >
                                      {subItem.label}
                                      <Image
                                        src="/images/nav-dot.svg"
                                        alt="nav dot"
                                        width={4}
                                        height={4}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                      />
                                    </Link>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </SubMenuItem>
                      ) : item.href === "/" ? (
                        <Link
                          href="/"
                          className="text-primary text-lg font-semibold font-greycliff uppercase"
                          onClick={handleLinkClick}
                        >
                          {item.label}
                        </Link>
                      ) : item.href === "#" ? (
                        <span className="text-primary text-lg font-semibold font-greycliff uppercase">
                          {item.label}
                        </span>
                      ) : (
                        <Link
                          href={item.href || "#"}
                          className="text-primary text-lg font-semibold font-greycliff uppercase"
                          onClick={handleLinkClick}
                        >
                          {item.label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="w-full lg:w-[20%] p-4 md:p-6 bg-[#ebd7a7]">
              <div>
                {data?.small_menu?.menu_items.map((menuItem, index) => {
                  const item = menuItem.menu_items_id;
                  const hasSubmenu = item.submenu && item.submenu.length > 0;

                  return (
                    <div key={index} className="mb-6">
                      <div className="text-primary text-lg font-semibold font-greycliff uppercase">
                        {item.href === "/" ? (
                          <Link href="/" onClick={handleLinkClick}>
                            {item.label}
                          </Link>
                        ) : item.href === "#" ? (
                          <span>{item.label}</span>
                        ) : (
                          <Link
                            href={item.href || "#"}
                            onClick={handleLinkClick}
                          >
                            {item.label}
                          </Link>
                        )}
                      </div>
                      {hasSubmenu && item.submenu && (
                        <ul className="space-y-4 mt-4">
                          {item.submenu.map((subItem, subIndex) => (
                            <li key={subIndex}>
                              {subItem.href === "/" ? (
                                <Link
                                  href="/"
                                  className="text-primary text-base font-light font-greycliff hover:font-medium transition-all group flex items-center gap-2"
                                  onClick={handleLinkClick}
                                >
                                  {subItem.label}
                                  <Image
                                    src="/images/nav-dot.svg"
                                    alt="nav dot"
                                    width={4}
                                    height={4}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  />
                                </Link>
                              ) : subItem.href === "#" ? (
                                <span className="text-primary text-base font-light font-greycliff hover:font-medium transition-all group flex items-center gap-2">
                                  {subItem.label}
                                  <Image
                                    src="/images/nav-dot.svg"
                                    alt="nav dot"
                                    width={4}
                                    height={4}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  />
                                </span>
                              ) : (
                                <Link
                                  href={subItem.href || "#"}
                                  className="text-primary text-base font-light font-greycliff hover:font-medium transition-all group flex items-center gap-2"
                                  onClick={handleLinkClick}
                                >
                                  {subItem.label}
                                  <Image
                                    src="/images/nav-dot.svg"
                                    alt="nav dot"
                                    width={4}
                                    height={4}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  />
                                </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                {data?.image?.image_file?.image?.id &&
                  data?.image?.image_file?.image?.filename_download && (
                    <div className="mt-6">
                      <Image
                        src={getReadableImageUrl(
                          data.image.image_file.image.id,
                          data.image.image_file.image.filename_download
                        )}
                        alt={data.image.alt}
                        width={287}
                        height={188}
                        className="w-full h-48 object-cover"
                        quality={90}
                      />
                    </div>
                  )}
                {data?.button2 && (
                  <ul className="mt-6">
                    <li>
                      {data.button2.href === "/" ? (
                        <Link
                          href="/"
                          className="text-primary text-lg font-semibold font-greycliff uppercase mt-4"
                          onClick={handleLinkClick}
                        >
                          {data.button2.label}
                        </Link>
                      ) : data.button2.href === "#" ? (
                        <span className="text-primary text-lg font-semibold font-greycliff uppercase mt-4">
                          {data.button2.label}
                        </span>
                      ) : (
                        <Link
                          href={data.button2.href || "#"}
                          className="text-primary text-lg font-semibold font-greycliff uppercase mt-4"
                          onClick={handleLinkClick}
                        >
                          {data.button2.label}
                        </Link>
                      )}
                    </li>
                  </ul>
                )}
              </div>
            </div>
            <div className="mt-6 lg:hidden px-4">
              {data?.button &&
                (data.button.href === "#" ? (
                  <span className="block w-full h-16 px-6 py-3 bg-primary text-white text-lg font-normal font-greycliff rounded-[51px] text-center leading-[2.5rem]">
                    {data.button.label}
                  </span>
                ) : (
                  <Link
                    href={data.button.href || "#"}
                    className="block w-full h-16 px-6 py-3 bg-primary text-white text-lg font-normal font-greycliff rounded-[51px] text-center leading-[2.5rem]"
                    onClick={handleLinkClick}
                  >
                    {data.button.label}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
